import React, {useState} from "react";
import {ScrollView} from "react-native";
import {
  TournamentPlayerBet_State, TournamentPlayerDetails, TournamentPlayerDetails_State,
  TournamentPrizeDistributionItem_AwardKind,
} from "../../generated/smg_service_pb";
import MainStyle from "../../MainStyle";
import Cell from "./RegisteredPlayersStatusHeaderCell";
import {formatCurrency, formatPayout} from "../utils";
import Div from "../lib/Div";
import {colors} from "../assets/styles/colors";
import P from "../lib/P";
import {isFullSwing} from "../constants";

const AwardKind = TournamentPrizeDistributionItem_AwardKind;

type Props = {
  mx?: string | number, maxH?: string | number, item: TournamentPlayerDetails[], w?: string | number, b_b_r?: number,
  playerId?: number, guaranteedPricePool: number
};

const RegisteredPlayersStatus = (props: Props) => {
  const {mx, maxH, item, w, b_b_r, playerId, guaranteedPricePool} = props;
  const [sortConfig, setSortConfig] = useState({key: 'rank', direction: 'ascending'});

  const renderPayout = (el: TournamentPlayerDetails) => {
    if (el.prizeDistributionItem === undefined) return '';

    if (el.prizeDistributionItem.kind === AwardKind.TICKET) {
      return `${el.prizeDistributionItem.additionalCashPrize ? formatCurrency(el.prizeDistributionItem.additionalCashPrize / 100) + ', ' : '$0'}\nTicket`;

    } else if (el.prizeDistributionItem.kind === AwardKind.STATIC) {
      return `${formatCurrency(el.prizeDistributionItem.staticAwardPrize / 100)}\n-`;

    } else if (el.prizeDistributionItem.kind === AwardKind.PHYSICAL_PRIZE) {
      return `${el.prizeDistributionItem.additionalCashPrize ? formatCurrency(el.prizeDistributionItem.additionalCashPrize / 100) + ', ' : '$0'}\nFS Kit`;

    } else {
      return `${formatCurrency(el.prizeDistributionItem.calculatedAwardPrize / 100)}\n-`;
    }
  };

  const requestSort = (key: string) => () => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({key, direction});
  };

  const getStatusText = (state: TournamentPlayerDetails_State) => {
    const DetailsState = TournamentPlayerDetails_State;

    if (state === DetailsState.PENDING) return 'PENDING';
    else if (state === DetailsState.PLAYING) return 'Playing';
    else if (state === DetailsState.FINISHED) return 'Complete';
    else return '-';
  };

  const compareNumbers = (a: TournamentPlayerDetails, b: TournamentPlayerDetails) => {
    if (sortConfig.key === 'rank' || sortConfig.key === 'points') {
      if (a.score > b.score) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a.score < b.score) return sortConfig.direction === 'ascending' ? 1 : -1;

    } else if (sortConfig.key === 'name') {
      if (a.playerName > b.playerName) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a.playerName < b.playerName) return sortConfig.direction === 'ascending' ? 1 : -1;

    } else if (sortConfig.key === 'status') {
      if (a.state > b.state) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a.state < b.state) return sortConfig.direction === 'ascending' ? 1 : -1;

    } else if (sortConfig.key === 'shots') {
      const BetState = TournamentPlayerBet_State;

      const a_shots = a.bets.filter(bet => bet.state === BetState.FINISHED).length;
      const b_shots = b.bets.filter(bet => bet.state === BetState.FINISHED).length;

      if (a_shots > b_shots) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a_shots < b_shots) return sortConfig.direction === 'ascending' ? 1 : -1;

    } else if (sortConfig.key === 'payout') {
      if (a.prize > b.prize) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a.prize < b.prize) return sortConfig.direction === 'ascending' ? 1 : -1;
    }

    return 0;
  };

  return (
    <Div rounded={isFullSwing ? 2 : 20} borderWidth={1} borderColor="#424242" pt={17} pb={20} px={12} mx={mx} w={w}
         bg={isFullSwing ? '#252526' : colors.settings_bg} maxH={maxH} roundedBottomLeft={b_b_r}
         roundedBottomRight={b_b_r}>
      {(isFullSwing && !!guaranteedPricePool) && (
        <Div row alignItems="center" justifyContent="center" mb={17}>
          <P fs={11} fontFamily="DavisSans-Medium" color="#27E9A6">GUARANTEED PRIZE POOL</P>
          <P fs={23} fontFamily="DavisSans-Medium" color="#27E9A6" ml={10}>{formatPayout(guaranteedPricePool / 100)}</P>
        </Div>
      )}

      <Div row mb={9} alignItems={isFullSwing ? 'flex-start' : undefined}>
        <Cell w={32} onPress={isFullSwing ? undefined : requestSort('rank')} mr={isFullSwing ? 9 : 0}
              active={isFullSwing ? undefined : sortConfig.key === 'rank'} dir={sortConfig.direction}>
          Rank
        </Cell>

        <Cell style={MainStyle.flex1} onPress={isFullSwing ? undefined : requestSort('name')} mr={isFullSwing ? 9 : 0}
              active={isFullSwing ? undefined : sortConfig.key === 'name'} dir={sortConfig.direction}>
          {isFullSwing ? `FIRST NAME /\nLAST NAME` : 'Name'}
        </Cell>

        <Cell w={80} onPress={isFullSwing ? undefined : requestSort('status')} mr={isFullSwing ? 9 : 0}
              active={isFullSwing ? undefined : sortConfig.key === 'status'} dir={sortConfig.direction}>
          Status
        </Cell>

        <Cell w={46} onPress={isFullSwing ? undefined : requestSort('points')} mr={isFullSwing ? 9 : 0}
              active={isFullSwing ? undefined : sortConfig.key === 'points'} dir={sortConfig.direction}>
          {isFullSwing ? 'Points / SHOTS' : 'Points'}
        </Cell>

        {!isFullSwing && (
          <Cell w={46} onPress={requestSort('shots')} active={sortConfig.key === 'shots'} dir={sortConfig.direction}>
            Shots
          </Cell>
        )}

        <Cell w={56} onPress={isFullSwing ? undefined : requestSort('payout')}
              active={isFullSwing ? undefined : sortConfig.key === 'payout'} dir={sortConfig.direction}>
          Payout
        </Cell>
      </Div>

      <ScrollView>
        {item.sort(compareNumbers).map(el => {
          const isYou = el.playerId === playerId;

          return (
            <Div row rounded={2} bg={!isYou ? '' : isFullSwing ? '' : colors.selectPurple} key={el.playerId}>
              <P ta="center" fw="500" lh={22} tt="uppercase" mr={isFullSwing ? 9 : 0}
                 color={!isYou ? '' : isFullSwing ? '#0AF' : colors.newGreenText} w={32}>
                {el.rank}
              </P>

              <P ta={isFullSwing ? undefined : 'center'} fw="500" lh={22} tt="uppercase" mr={isFullSwing ? 9 : 0}
                 color={!isYou ? '' : isFullSwing ? '#0AF' : colors.newGreenText} flex={1}
                 numberOfLines={isFullSwing ? 2 : 1}>
                {isFullSwing ? `${el.playerFirstName}\n${el.playerLastName}` : el.playerName || '-'}
              </P>

              <P ta={isFullSwing ? undefined : 'center'} fw="500" lh={22} tt="uppercase" mr={isFullSwing ? 9 : 0}
                 color={!isYou ? '' : isFullSwing ? '#0AF' : colors.newGreenText} fs={15} w={80}>
                {getStatusText(el.state)}
              </P>

              <P ta={isFullSwing ? undefined : 'center'} fw="500" lh={22} tt="uppercase" mr={isFullSwing ? 9 : 0}
                 color={!isYou ? '' : isFullSwing ? '#0AF' : colors.newGreenText} w={46}>
                {isFullSwing ? `${el.score} /\n${el.bets.filter(bet => bet.state === TournamentPlayerBet_State.FINISHED && bet.resolved).length}` : el.score}
              </P>

              {!isFullSwing && (
                <P ta="center" fw="500" lh={22} tt="uppercase"
                   color={!isYou ? '' : isFullSwing ? '#0AF' : colors.newGreenText} w={46}>
                  {el.bets.filter(bet => bet.state === TournamentPlayerBet_State.FINISHED && bet.resolved).length}
                </P>
              )}

              <P ta={isFullSwing ? undefined : 'center'} fw="500" lh={22} tt="uppercase"
                 color={!isYou ? '' : isFullSwing ? '#0AF' : colors.newGreenText} w={56}>
                {el.prizeDistributionItem !== undefined ? renderPayout(el) : el.prize ? formatCurrency(el.prize / 100) : `$0\n-`}
              </P>
            </Div>
          );
        })}
      </ScrollView>
    </Div>
  );
};

export default React.memo(RegisteredPlayersStatus);
